import { useCallback, useEffect, useRef, useState } from 'react';

import { queryParams } from '../../../organisations/users/organisationUserFormat';

const SEARCH_DELAY_MS = 300;

export const DEFAULT_QUERY = {
    search: null,
    role_id: null,
    department_id: null,
    venue_id: null,
    module_id: null,
    last_login: 'any',
    per_page: 25,
};

/**
 * The Manage Users table: its search and filters, the current page of rows
 * and the paginator meta beside them.
 */
export default function useProjectUserList({ listUrl }) {
    const [query, setQueryState] = useState(DEFAULT_QUERY);
    const [searchInput, setSearchInput] = useState('');
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const readSeq = useRef(0);

    const setQuery = useCallback((change) => {
        setQueryState((previous) => ({ ...previous, ...change }));
        setPage(1);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            const search = searchInput.trim() === '' ? null : searchInput.trim();

            if (search !== query.search) {
                setQuery({ search });
            }
        }, SEARCH_DELAY_MS);

        return () => clearTimeout(timer);
    }, [searchInput, query.search, setQuery]);

    useEffect(() => {
        const sequence = ++readSeq.current;

        setLoading(true);
        setError(null);

        axios
            .get(listUrl, { params: { ...queryParams(query), page } })
            .then(({ data }) => {
                // Only the newest read may land, so the table never lags the filters above it.
                if (sequence === readSeq.current) {
                    setRows(data.data);
                    setMeta(data.meta);
                }
            })
            .catch(() => {
                if (sequence === readSeq.current) {
                    setError($t('Something went wrong'));
                }
            })
            .finally(() => {
                if (sequence === readSeq.current) {
                    setLoading(false);
                }
            });
    }, [listUrl, query, page, reloadKey]);

    const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

    return { query, setQuery, searchInput, setSearchInput, page, setPage, rows, meta, loading, error, refresh };
}
